import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles, type Article } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(slug || categories[0].slug);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  const currentCategory = categories.find((c) => c.slug === activeTab);

  useEffect(() => {
    setActiveTab(slug || categories[0].slug);
  }, [slug]);

  useEffect(() => {
    setFilteredArticles(articles.filter((a) => a.categorySlug === activeTab));
  }, [activeTab]);

  const handleTabChange = (newSlug: string) => {
    setActiveTab(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yandex-contest.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory?.name || '' },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${currentCategory?.name} - 每日大赛Yandex官网`,
    description: currentCategory?.description,
    url: `https://yandex-contest.com/category/${activeTab}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory?.name || '分类' },
                ]}
              />
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-4">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
                {currentCategory?.description}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-2 py-6 overflow-x-auto flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-7xl mx-auto">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                        {currentCategory?.name}
                      </span>
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors font-heading line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-time-line"></i>
                      <span>阅读约{article.readTime}分钟</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}